import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { PageFilterType } from '@common/paged-data/enums/page-filter-type.enum';
import { StringService } from '@portal-core/general/services/string.service';
import { LicenseUserClientSideInfoKey } from '@portal-core/license-users/enums/license-user-client-side-info-key.enum';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { LicenseUsersService } from '@portal-core/license-users/services/license-users.service';
import { Project } from '@portal-core/projects/models/project.model';
import { ProjectsService } from '@portal-core/projects/services/projects.service';
import { TaskStatus } from '@portal-core/tasks/enums/task-status.enum';
import { TaskMilestone } from '@portal-core/tasks/enums/tasks-milestone.enum';
import { TaskPriority } from '@portal-core/tasks/enums/tasks-priority.enum';
import { TaskFilters } from '@portal-core/tasks/models/task-filters.model';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { isEqual } from 'lodash';
import { combineLatest, Observable, startWith, Subscription } from 'rxjs';

interface TaskFilterForm {
  startDate: FormControl<string>;
  startTime: FormControl<string>;
  dueDate: FormControl<string>;
  dueTime: FormControl<string>;
  dates: FormControl<PageFilterType[]>;
  licenseUsers: FormControl<string[]>;
  priorities: FormControl<TaskPriority[]>;
  projects: FormControl<number[]>;
  statuses: FormControl<TaskStatus[]>;
  milestones: FormControl<TaskMilestone[]>;
}

export enum TaskTime {
  StartTime = 'StartTime',
  EndTime = 'EndTime'
}

@AutoUnsubscribe()
@Component({
  selector: 'mc-task-filter-dialog',
  templateUrl: './task-filter-dialog.component.html',
  styleUrls: ['./task-filter-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TaskFilterDialogComponent extends DialogBase implements OnInit {
  static DialogConfig: MatDialogConfig = {
    width: '63rem',
    height: '60rem'
  };
  static CalendarDialogConfig: MatDialogConfig = {
    width: '63rem',
    height: '75rem'
  };

  TaskPriority: typeof TaskPriority = TaskPriority;
  FilterType: typeof PageFilterType = PageFilterType;
  TaskStatus: typeof TaskStatus = TaskStatus;
  TaskMilestone: typeof TaskMilestone = TaskMilestone;
  LicenseUserClientSideInfoKey: typeof LicenseUserClientSideInfoKey = LicenseUserClientSideInfoKey;
  licenseUsers$: Observable<LicenseUser[]>;
  projects$: Observable<Project[]>;

  currentInfoKey: LicenseUserClientSideInfoKey;
  private originalFilters: TaskFilters;
  startSubscription: Subscription;
  dueSubscription: Subscription;
  private previousStartTime: string;
  private previousStartDate: string;
  private previousDueTime: string;
  private previousDueDate: string;

  taskFilterForm: FormGroup<TaskFilterForm>;

  constructor(
    private projectsService: ProjectsService,
    private licenseUsersService: LicenseUsersService,
    private stringService: StringService,
    public dialogRef: MatDialogRef<TaskFilterDialogComponent>,
    public dialog: MatDialog,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any) {
    super(dialog, dialogRef);
  }

  ngOnInit() {
    super.ngOnInit();
    this.originalFilters = this.data.filters;
    this.createTaskFilterForm();

    const licenseId = this.data.licenseId;
    this.licenseUsers$ = this.licenseUsersService.getAllLicenseUsersByLicenseId$(licenseId, false);
    this.projects$ = this.projectsService.getProjectsList$(licenseId);
    this.currentInfoKey = this.data.currentInfoKey;

    const startDate = this.taskFilterForm.get('startDate');
    const startTime = this.taskFilterForm.get('startTime');
    this.startSubscription = combineLatest([
      startDate.valueChanges.pipe(startWith(startDate.value)),
      startTime.valueChanges.pipe(startWith(startTime.value))
    ]).subscribe(() => this.onTimeOrDateValueChanged(TaskTime.StartTime));

    const dueDate = this.taskFilterForm.get('dueDate');
    const dueTime = this.taskFilterForm.get('dueTime');
    this.dueSubscription = combineLatest([
      dueDate.valueChanges.pipe(startWith(dueDate.value)),
      dueTime.valueChanges.pipe(startWith(dueTime.value))
    ]).subscribe(() => this.onTimeOrDateValueChanged(TaskTime.EndTime));
  }
  protected createTaskFilterForm() {
    let startDate: string;
    let startTime: string;
    let dueDate: string;
    let dueTime: string;
    let priorities: TaskPriority[];
    let dates: PageFilterType[];
    let licenseUsers: string[];
    let projects: number[];
    let statuses: TaskStatus[];
    let milestones: TaskMilestone[];
    if (this.originalFilters) {
      // start date
      const customStartDate: Date = new Date(this.originalFilters.CustomStartDate);
      if (this.originalFilters.CustomStartDate) {
        startDate = new Date(customStartDate.getFullYear(), customStartDate.getMonth(), customStartDate.getDate()).toISOString();
        startTime = this.stringService.formatDate(customStartDate, 'HH:mm');
      }

      // due date
      const customDueDate: Date = new Date(this.originalFilters.CustomDueDate);
      if (this.originalFilters.CustomDueDate) {
        dueDate = new Date(customDueDate.getFullYear(), customDueDate.getMonth(), customDueDate.getDate()).toISOString();
        dueTime = this.stringService.formatDate(customDueDate, 'HH:mm');
      }

      // others
      priorities = this.originalFilters.Priorities;
      dates = this.originalFilters.Dates;
      licenseUsers = this.originalFilters.Users;
      projects = this.originalFilters.Projects;
      statuses = this.originalFilters.Statuses;
      milestones = this.originalFilters.Milestones;
    }

    this.taskFilterForm = this.fb.group({
      startDate: new FormControl(startDate),
      startTime: new FormControl(startTime),
      dueDate: new FormControl(dueDate),
      dueTime: new FormControl(dueTime),
      dates: new FormControl(dates),
      licenseUsers: new FormControl(licenseUsers),
      priorities: new FormControl(priorities),
      projects: new FormControl(projects),
      statuses: new FormControl(statuses),
      milestones: new FormControl(milestones)
    });
  }

  onTimeOrDateValueChanged(taskTime: TaskTime) {
    const startTime = this.taskFilterForm.get('startTime').value;
    const startDate = this.taskFilterForm.get('startDate').value;

    const dueTime = this.taskFilterForm.get('dueTime').value;
    const dueDate = this.taskFilterForm.get('dueDate').value;

    if (taskTime === TaskTime.StartTime) {
      // Skip auto-fill if a field is being cleared (was set, now is not)
      if (this.previousStartTime && !startTime) {
        this.previousStartTime = startTime;
        this.previousStartDate = startDate;
        return;
      }
      if (this.previousStartDate && !startDate) {
        this.previousStartTime = startTime;
        this.previousStartDate = startDate;
        return;
      }

      if (startTime && !startDate) {
        this.taskFilterForm.get('startDate').setValue(new Date().toISOString(), { emitEvent: false });
      } else if (startDate && !startTime) {
        this.taskFilterForm.get('startTime').setValue('00:00', { emitEvent: false });
      }

      this.previousStartTime = startTime;
      this.previousStartDate = startDate;
    } else {
      // Skip auto-fill if a field is being cleared (was set, now is not)
      if (this.previousDueTime && !dueTime) {
        this.previousDueTime = dueTime;
        this.previousDueDate = dueDate;
        return;
      }
      if (this.previousDueDate && !dueDate) {
        this.previousDueTime = dueTime;
        this.previousDueDate = dueDate;
        return;
      }

      if (dueTime && !dueDate) {
        this.taskFilterForm.get('dueDate').setValue(new Date().toISOString(), { emitEvent: false });
      } else if (dueDate && !dueTime) {
        this.taskFilterForm.get('dueTime').setValue('00:00', { emitEvent: false });
      }

      this.previousDueTime = dueTime;
      this.previousDueDate = dueDate;
    }
  }

  onSubmit() {

    // create the full start date using both time and date inputs
    let customStartDate: string;
    if (this.taskFilterForm.get('startDate').value) {
      //turn it into a date to combine the time and the date better together
      const [hours, minutes] = (this.taskFilterForm.get('startTime').value).split(':').map(Number);
      const customStartTimeValue = new Date();
      customStartTimeValue.setHours(hours, minutes, 0, 0);

      let customStartDateValue = new Date(this.taskFilterForm.get('startDate').value);

      customStartDate = new Date(
        customStartDateValue.getFullYear(),
        customStartDateValue.getMonth(),
        customStartDateValue.getDate(),
        customStartTimeValue.getHours(),
        customStartTimeValue.getMinutes()
      ).toISOString();
    }

    let customDueDate: string;
    if (this.taskFilterForm.get('dueDate').value) {
      //turn it into a date to combine the time and the date better together
      const [hours, minutes] = this.taskFilterForm.get('dueTime').value.split(':').map(Number);
      const customDueTimeValue = new Date();
      customDueTimeValue.setHours(hours, minutes, 0, 0);

      let customDueDateValue = new Date(this.taskFilterForm.get('dueDate').value);
      customDueDate = new Date(
        customDueDateValue.getFullYear(),
        customDueDateValue.getMonth(),
        customDueDateValue.getDate(),
        customDueTimeValue.getHours(),
        customDueTimeValue.getMinutes()
      ).toISOString();
    }

    // set values to submit
    const filters: TaskFilters = {
      Priorities: this.taskFilterForm.get('priorities').value?.length > 0 ? this.taskFilterForm.get('priorities').value : undefined,
      Dates: this.taskFilterForm.get('dates').value?.length > 0 ? this.taskFilterForm.get('dates').value : undefined,
      Users: this.taskFilterForm.get('licenseUsers').value?.length > 0 ? this.taskFilterForm.get('licenseUsers').value : undefined,
      Projects: this.taskFilterForm.get('projects').value?.length > 0 ? this.taskFilterForm.get('projects').value : undefined,
      Statuses: this.taskFilterForm.get('statuses').value?.length > 0 ? this.taskFilterForm.get('statuses').value : undefined,
      Milestones: this.taskFilterForm.get('milestones').value?.length > 0 ? this.taskFilterForm.get('milestones').value : undefined,
      CustomStartDate: customStartDate,
      CustomDueDate: customDueDate
    };

    if (isEqual(this.originalFilters, filters)) {
      this.closeDialog();
      return;
    }

    this.closeDialog(filters);
  }
}
